/**
 * Hover-intent prefetch helper for product tiles.
 *
 * Product tiles render as plain `<a href>` (not `next/link`), so Next.js does
 * no automatic route/data prefetch. This warms the product route's JS bundle
 * and its SSG/ISR data on hover so the click lands on an already-generated
 * page instead of triggering an on-demand `fallback: true` render.
 *
 * Dedupe is module-level (shared across every tile) so each product URL is
 * warmed at most once per session — a mouse sweeping across a dense grid won't
 * re-warm the same cards, and Next's own prefetch cache is only ever asked for
 * a given URL once.
 */
const prefetchedUrls = new Set<string>();

/** True for internal product-detail routes only (skips jobs / external links). */
const isProductUrl = (url: string): boolean => url.startsWith('/product/');

/**
 * Warm `url` via the provided router prefetch, guarding to internal product
 * routes and skipping URLs already warmed this session.
 *
 * @param url      candidate href from a listing tile
 * @param prefetch router prefetch fn (from `useRouterContext`)
 */
export function prefetchProductUrl(
  url: string | undefined,
  prefetch: (url: string) => void,
): void {
  if (!url || !isProductUrl(url) || prefetchedUrls.has(url)) return;
  prefetchedUrls.add(url);
  prefetch(url);
}

/** Test-only: clear the module-level dedupe cache between cases. */
export function __resetPrefetchedUrls(): void {
  prefetchedUrls.clear();
}
